import React, { useEffect, useRef, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import Image from "next/image";
import { BackgroundIcon, DeleteIcon, Download, Edit, EllipsisBubble, ExpandIcon, FaceRecognition, HeartFilled, HeartOutline, InpaintIcon, ShareIcon, SwapIcon, TrophyIcon } from "components/atoms/Icon";
import CustomJoyride from "components/atoms/Joyride";
import PopoverWithChildren from "components/atoms/Popover";
import { useTutorial } from "components/organisms/TutorialProvider";
import { controlsContainerTutorialSteps } from "utils/joyride/controlsContainerTutorialSteps";
import { Span } from "utils/styling/typography.styled";
import * as Styled from "./ControlsContainer.styled";
interface ControlsContainerProps {
  isFirstImage?: boolean;
  imageInfo: any;
  toggles: {
    [key: string]: boolean;
  };
  handleToggle: (id: string) => void;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  handleCompetitionsClick: (imageId?: string) => void;
  handleEditClick: (imageId?: string) => void;
  handleADetailerClick: (imageId?: string) => void;
  handleFaceSwapClick: (imageId?: string) => void;
  handleBGRemovalClick: (imageId?: string) => void;
  handleInpaintClick: (imageId?: string) => void;
  handleUpscaleClick: (imageId?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  isUserImage: (username: string) => boolean;
}
const ControlsContainer: React.FC<ControlsContainerProps> = ({
  isFirstImage,
  imageInfo,
  toggles,
  handleToggle,
  handleShareClick,
  handleCompetitionsClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  prepareForDelete,
  handleVote,
  isUserImage
}) => {
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (tutorialState.imageViewer.completed && !tutorialState.controlsContainer.completed && !tutorialState.controlsContainer.started && isFirstImage) {
      startTutorial("controlsContainer");
    }
  }, [tutorialState, startTutorial, isFirstImage, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.controlsContainer.started && !tutorialState.controlsContainer.completed && isFirstImage) {
      setRun(true);
    }
  }, [isFirstImage, tutorialState]);
  const handleControlsTutorialCallback = (data: {
    status: any;
    type: any;
    action: any;
    index: any;
  }) => {
    const {
      status,
      type,
      action,
      index
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      // Tutorial has finished or been skipped
      completeTutorial("controlsContainer");
      setRun(false);
      setStepIndex(0);
      const event = new MouseEvent("click", {
        bubbles: true
      });
      handleShareClick(event as unknown as React.MouseEvent<Element, MouseEvent>, imageInfo.id);
    } else if (type === EVENTS.STEP_BEFORE) {
      // Handle actions before each step
      const currentStep = controlsContainerTutorialSteps[index];
      if (currentStep.target === ".joyride-ellipsis-button" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      if (currentStep.target === ".joyride-heart-icon" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      // Add other actions for steps within ControlsContainer
    } else if (type === EVENTS.STEP_AFTER) {
      // Update the step index after each step
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  return <div className={isFirstImage ? "joyride-controls-container" : ""} data-sentry-component="ControlsContainer" data-sentry-source-file="index.tsx">
            <CustomJoyride steps={controlsContainerTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling callback={handleControlsTutorialCallback} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
            <Styled.ControlsContainer className={isFirstImage ? "joyride-controls-bar" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Icon id="shareIcon" aria-label="Share this image" onClick={e => handleShareClick(e, imageInfo.id)} className={isFirstImage ? "joyride-share-button" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.IconWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Image height={24} width={24} priority src={ShareIcon} alt="Share" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                    </Styled.IconWrapper>
                </Styled.Icon>
                <PopoverWithChildren ref={popoverButtonRef} buttonNode={<Styled.Icon className={isFirstImage ? "joyride-ellipsis-button" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={EllipsisBubble} alt="Edit" />
                            </Styled.IconWrapper>
                        </Styled.Icon>} data-sentry-element="PopoverWithChildren" data-sentry-source-file="index.tsx">
                    <a href={imageInfo.image} download target="_blank" rel="noreferrer">
                        <Styled.PopoverItem className={isFirstImage ? "joyride-download-option" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.IconWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Image height={24} width={24} priority src={Download} alt="Download" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                            </Styled.IconWrapper>
                            <span>Download</span>
                        </Styled.PopoverItem>
                    </a>
                    {isUserImage(imageInfo.imageMetadata.username) && <>
                            <Styled.PopoverItem onClick={() => handleCompetitionsClick(imageInfo.id)} className={isFirstImage ? "joyride-competition-option" : ""}>
                                <Styled.IconWrapper>
                                    <Image height={24} width={24} priority src={TrophyIcon} alt="Competition" />
                                </Styled.IconWrapper>

                                <span>Enter Competition</span>
                            </Styled.PopoverItem>
                        </>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleInpaintClick(imageInfo.id)}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={Edit} alt="Edit" />
                            </Styled.IconWrapper>
                            <span>Edit</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => prepareForDelete(imageInfo.id)} className={isFirstImage ? "joyride-delete-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={DeleteIcon} alt="Delete" />
                            </Styled.IconWrapper>
                            <span>Delete</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleADetailerClick(imageInfo.id)} className={isFirstImage ? "joyride-refine-faces-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={FaceRecognition} alt="Refine Faces" />
                            </Styled.IconWrapper>
                            <span>Refine Faces</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleFaceSwapClick(imageInfo.id)} className={isFirstImage ? "joyride-swap-faces-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={SwapIcon} alt="Swap Faces" />
                            </Styled.IconWrapper>
                            <span>Swap Faces</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleBGRemovalClick(imageInfo.id)} className={isFirstImage ? "joyride-remove-bg-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={BackgroundIcon} alt="Remove Background" />
                            </Styled.IconWrapper>
                            <span>Remove BG</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleInpaintClick(imageInfo.id)} className={isFirstImage ? "joyride-spot-edit-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={InpaintIcon} alt="Inpainting" />
                            </Styled.IconWrapper>
                            <span>Spot Edit</span>
                        </Styled.PopoverItem>}
                    {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleUpscaleClick(imageInfo.id)} className={isFirstImage ? "joyride-upscale-option" : ""}>
                            <Styled.IconWrapper>
                                <Image height={24} width={24} priority src={ExpandIcon} alt="Upscaling" />
                            </Styled.IconWrapper>
                            <span>Upscale Image</span>
                        </Styled.PopoverItem>}
                </PopoverWithChildren>
                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem className={isFirstImage ? "joyride-public-toggle" : ""}>
                        <Styled.SmallToggleSwitch>
                            <input id={`visibilityToggle-${imageInfo.id}`} type="checkbox" checked={toggles[imageInfo.id]} onChange={() => handleToggle(imageInfo.id)} />
                            <span className="slider round"></span>
                        </Styled.SmallToggleSwitch>
                        <span>
                            {toggles[imageInfo.id] ? "Public" : "Private"}
                        </span>
                    </Styled.PopoverItem>}
                <Styled.ReactContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Icon key={imageInfo.id} onClick={() => handleVote(imageInfo.id)} className={isFirstImage ? "joyride-heart-icon" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.HeartCheckbox data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                        <Styled.IconWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {imageInfo.isVotedByUser ? <Image height={24} width={24} priority src={HeartFilled} alt="Heart" /> : <Image height={24} width={24} priority src={HeartOutline} alt="Heart" />}
                        </Styled.IconWrapper>
                    </Styled.Icon>
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">{imageInfo.voteCount}</Span>
                </Styled.ReactContainer>
            </Styled.ControlsContainer>
        </div>;
};
export default ControlsContainer;